import { action, makeObservable, observable } from "mobx";
import { Dayjs } from "dayjs";
import PodOrderAPI from "../api/PodOrderAPI";
import { FilterValue } from "../models/Filters/FilterValue";
import {
  GetPodOrdersParams,
  PodOrder,
  getPodOrderRowKey,
  isSamePodOrderRow,
  PodOrderBatchInventoryAllocateBody,
  PodOrderBatchPrintInwardBody,
  PodOrderBatchPrintInwardItem,
  PodOrderBatchPrintOutwardBody,
  PodOrderCsvImportResult,
  PodOrderListingResponse,
  PodOrderPrintOutwardConfirmPayload,
  PodOrderPrintingFileResponce,
  PodOrderPrintingFilesBody,
  PodOrderPrintingFilesResponseItem,
} from "../models/PodOrder/PodOrder";
import { toPodPrintingTypeName } from "../models/PodOrder/PodPrintingType";
import { OrderMockup } from "../models/Order/OrderMockup";
import {
  mapPodOrderMockupsToOrderMockups,
  type PodOrderMockupPreviewLine,
} from "../models/PodOrder/PodOrderMockupPreview";
import { PodOrderProgressStage } from "../models/PodOrder/PodOrderProgressStage";
import { APIResponse } from "../models/NetworkResponse";

export type PodOrderListParams = GetPodOrdersParams & { tab_id: string };

export const DEFAULT_POD_ORDER_LIST_PARAMS: PodOrderListParams = {
  page: 1,
  limit: 30,
  status: PodOrderProgressStage.INVENTORY_ALLOCATED,
  sku: "",
  start_date: "",
  end_date: "",
  tab_id: "allocated-orders",
  sort_by: "order_id",
  sort_type: "DESC",
  order_ids: [],
  printing_type: "",
};

const parseOrderIdsParam = (value: string | null): number[] => {
  if (!value) return [];
  return value
    .split(",")
    .map(id => parseInt(id.trim(), 10))
    .filter(id => !Number.isNaN(id));
};

const parseOrderIdsFromSearch = (searchParams: URLSearchParams): number[] => {
  const fromRepeated = searchParams.getAll("order_id");
  if (fromRepeated.length) {
    return fromRepeated
      .map(id => parseInt(id.trim(), 10))
      .filter(id => !Number.isNaN(id));
  }
  return parseOrderIdsParam(searchParams.get("order_ids"));
};

const parsePrintingTypeFromSearch = (searchParams: URLSearchParams): string => {
  const value = searchParams.get("printing_types")?.trim() ?? "";
  return value ? toPodPrintingTypeName(value) : "";
};

export class PodOrderStore {
  podOrderAPI: PodOrderAPI;
  podOrderListingResponse: PodOrderListingResponse;
  printOutwardLines: PodOrderPrintingFilesResponseItem[];
  printOutwardPrintingFiles: PodOrderPrintingFileResponce[];
  loading: boolean;
  uploadingPodOrders: boolean;
  printOutwardFilesLoading: boolean;
  error: string | null;
  params: PodOrderListParams;
  filtersOpen: boolean;
  draftSearch: string;
  draftOrderIds: number[];
  draftPrintingType: string;
  selectedOrders: PodOrder[];
  confirmLoading: boolean;
  printOutwardDrawerOpen: boolean;
  printOutwardPrinter: FilterValue | null;
  printOutwardDueDate: Dayjs | null;
  printOutwardNotes: string;
  printOutwardPrinterError: boolean;
  printOutwardDueDateError: boolean;
  printOutwardError: string | null;
  printInwardDrawerOpen: boolean;
  printInwardError: string | null;
  printInwardExpandedKey: string | null;
  printInwardLines: PodOrder[];
  printInwardItems: Map<string, PodOrderBatchPrintInwardItem>;
  printInwardRejectionFiles: Map<string, File[]>;
  printInwardRejectionNotesErrors: Set<string>;
  printInwardMockupPreviewOpen: boolean;
  printInwardMockupPreviewLoading: boolean;
  printInwardMockupPreviewMockups: OrderMockup[];
  printInwardMockupPreviewProduct: { id: number; name: string } | null;
  podOrderCsvFile: File | null;
  podOrderCsvImportResult: PodOrderCsvImportResult | null;

  constructor() {
    this.podOrderAPI = new PodOrderAPI();
    this.podOrderListingResponse = {
      list: [],
      total_count: 0,
      limit: 0,
      total_pages: 0,
    };
    this.printOutwardLines = [];
    this.printOutwardPrintingFiles = [];
    this.loading = false;
    this.uploadingPodOrders = false;
    this.printOutwardFilesLoading = false;
    this.error = null;
    this.params = { ...DEFAULT_POD_ORDER_LIST_PARAMS };
    this.filtersOpen = false;
    this.draftSearch = "";
    this.draftOrderIds = [];
    this.draftPrintingType = "";
    this.selectedOrders = [];
    this.confirmLoading = false;
    this.printOutwardDrawerOpen = false;
    this.printOutwardPrinter = null;
    this.printOutwardDueDate = null;
    this.printOutwardNotes = "";
    this.printOutwardPrinterError = false;
    this.printOutwardDueDateError = false;
    this.printOutwardError = null;
    this.printInwardDrawerOpen = false;
    this.printInwardError = null;
    this.printInwardExpandedKey = null;
    this.printInwardLines = [];
    this.printInwardItems = new Map();
    this.printInwardRejectionFiles = new Map();
    this.printInwardRejectionNotesErrors = new Set();
    this.printInwardMockupPreviewOpen = false;
    this.printInwardMockupPreviewLoading = false;
    this.printInwardMockupPreviewMockups = [];
    this.printInwardMockupPreviewProduct = null;
    this.podOrderCsvFile = null;
    this.podOrderCsvImportResult = null;

    makeObservable(this, {
      podOrderListingResponse: observable,
      printOutwardLines: observable,
      printOutwardPrintingFiles: observable,
      loading: observable,
      uploadingPodOrders: observable,
      printOutwardFilesLoading: observable,
      error: observable,
      params: observable,
      filtersOpen: observable,
      draftSearch: observable,
      draftOrderIds: observable,
      draftPrintingType: observable,
      selectedOrders: observable,
      confirmLoading: observable,
      printOutwardDrawerOpen: observable,
      printOutwardPrinter: observable,
      printOutwardDueDate: observable,
      printOutwardNotes: observable,
      printOutwardPrinterError: observable,
      printOutwardDueDateError: observable,
      printOutwardError: observable,
      printInwardDrawerOpen: observable,
      printInwardError: observable,
      printInwardExpandedKey: observable,
      printInwardLines: observable,
      printInwardItems: observable,
      printInwardRejectionFiles: observable,
      printInwardRejectionNotesErrors: observable,
      printInwardMockupPreviewOpen: observable,
      printInwardMockupPreviewLoading: observable,
      printInwardMockupPreviewMockups: observable,
      printInwardMockupPreviewProduct: observable,
      podOrderCsvFile: observable,
      podOrderCsvImportResult: observable,
      getPodOrders: action,
      getPrintingFiles: action,
      getPodOrderMockups: action,
      batchInventoryAllocate: action,
      batchPrintOutward: action,
      batchPrintInward: action,
      uploadPodOrdersFromCSV: action,
      initializeParamsFromSearch: action,
      setParams: action,
      setFiltersOpen: action,
      toggleFiltersOpen: action,
      setDraftSearch: action,
      applySearch: action,
      applyFilters: action,
      clearFilters: action,
      setDraftOrderIds: action,
      setDraftPrintingType: action,
      handlePageChange: action,
      handleRowsPerPageChange: action,
      handleSortChange: action,
      clearSelection: action,
      selectAll: action,
      toggleRow: action,
      setConfirmLoading: action,
      setPodOrderCsvFile: action,
      clearPodOrderCsvFile: action,
      setPodOrderCsvImportResult: action,
      clearPodOrderCsvImportResult: action,
      setPrintOutwardDrawerOpen: action,
      setPrintOutwardError: action,
      openPrintOutwardDrawer: action,
      resetPrintOutwardForm: action,
      setPrintOutwardPrinter: action,
      setPrintOutwardDueDate: action,
      setPrintOutwardNotes: action,
      validatePrintOutwardForm: action,
      batchInventoryAllocateSelected: action,
      batchPrintOutwardSelected: action,
      setPrintInwardDrawerOpen: action,
      setPrintInwardError: action,
      openPrintInwardDrawer: action,
      setPrintInwardExpandedKey: action,
      togglePrintInwardExpandedKey: action,
      openPrintInwardMockupPreview: action,
      openPrintOutwardMockupPreview: action,
      openPodOrderMockupPreview: action,
      closePrintInwardMockupPreview: action,
      resetPrintInwardForm: action,
      updatePrintInwardLine: action,
      setPrintInwardRejectionNotes: action,
      setPrintInwardRejectionImages: action,
      setPrintInwardRejectionImageUrls: action,
      removePrintInwardRejectionImage: action,
      validatePrintInwardForm: action,
      batchPrintInwardSelected: action,
    });
  }

  // --- API (PodOrderAPI) ---

  getPodOrders = async (params: GetPodOrdersParams = this.params) => {
    this.loading = true;
    this.error = null;
    try {
      const response = await this.podOrderAPI.getPodOrders(params);
      this.podOrderListingResponse = {
        list: response.list ?? [],
        total_count: response.total_count ?? 0,
        limit: response.limit ?? 0,
        total_pages: response.total_pages ?? 0,
      };
      return this.podOrderListingResponse;
    } catch (error: any) {
      this.error =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Error fetching POD Orders";
    } finally {
      this.loading = false;
    }
  };


  getPrintingFiles = async (
    body: PodOrderPrintingFilesBody,
  ): Promise<APIResponse> => {
    try {
      const response = await this.podOrderAPI.getPrintingFiles(body);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  getPodOrderMockups = async (params: PodOrderMockupPreviewLine & { sku: string }) => {
    return this.podOrderAPI.getPodOrderMockups({
      order_id: params.order_id,
      product_id: params.product_id,
      sku: params.sku,
    });
  };

  batchInventoryAllocate = async (
    body: PodOrderBatchInventoryAllocateBody,
  ): Promise<APIResponse> => {
    try {
      const response = await this.podOrderAPI.batchInventoryAllocate(body);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  batchPrintOutward = async (
    body: PodOrderBatchPrintOutwardBody,
  ): Promise<APIResponse> => {
    try {
      const response = await this.podOrderAPI.batchPrintOutward(body);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  batchPrintInward = async (
    body: PodOrderBatchPrintInwardBody,
  ): Promise<APIResponse> => {
    try {
      const response = await this.podOrderAPI.batchPrintInward(body);
      return response;
    } catch (error: any) {
      throw error;
    }
  };

  uploadPodOrdersFromCSV = async (file: File): Promise<APIResponse | undefined> => {
    this.uploadingPodOrders = true;
    this.error = null;
    try {
      const response = await this.podOrderAPI.uploadPodOrdersCSV(file);
      return response;
    } catch (error: any) {
      this.error =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to upload POD Orders";
      throw error;
    } finally {
      this.uploadingPodOrders = false;
    }
  };

  // --- List & filters ---

  initializeParamsFromSearch = (
    searchParams: URLSearchParams,
    resolvedTabId: string,
    resolvedStatus: string,
  ) => {
    this.params = {
      page: parseInt(searchParams.get("page") || "1", 10),
      limit: parseInt(searchParams.get("limit") || "30", 10),
      status: resolvedStatus,
      sku: searchParams.get("sku") || searchParams.get("search") || "",
      start_date: searchParams.get("start_date") || "",
      end_date: searchParams.get("end_date") || "",
      tab_id: resolvedTabId,
      sort_by: searchParams.get("sort_by") || "order_id",
      sort_type: (searchParams.get("sort_type") as "ASC" | "DESC") || "DESC",
      order_ids: parseOrderIdsFromSearch(searchParams),
      printing_type: parsePrintingTypeFromSearch(searchParams),
    };
    this.draftSearch =
      searchParams.get("sku") || searchParams.get("search") || "";
    this.draftOrderIds = [...(this.params.order_ids ?? [])];
    this.draftPrintingType = this.params.printing_type ?? "";
  };

  setParams = (
    updater:
      | Partial<PodOrderListParams>
      | ((prev: PodOrderListParams) => PodOrderListParams),
  ) => {
    const next =
      typeof updater === "function" ? updater(this.params) : { ...this.params, ...updater };
    const selectionReset =
      next.page !== this.params.page || next.tab_id !== this.params.tab_id;
    this.params = next;
    if (selectionReset) this.clearSelection();
  };

  setFiltersOpen = (open: boolean) => {
    this.filtersOpen = open;
  };

  toggleFiltersOpen = () => {
    this.filtersOpen = !this.filtersOpen;
  };

  setDraftSearch = (value: string) => {
    this.draftSearch = value;
    if (!value) this.setParams({ sku: "", page: 1 });
  };

  applySearch = () => {
    this.setParams({ sku: this.draftSearch.trim(), page: 1 });
  };

  clearFilters = () => {
    this.draftSearch = "";
    this.draftOrderIds = [];
    this.draftPrintingType = "";
    this.setParams({
      sku: "",
      start_date: "",
      end_date: "",
      order_ids: [],
      printing_type: "",
      page: 1,
    });
  };

  setDraftOrderIds = (orderIds: number[]) => {
    this.draftOrderIds = orderIds;
  };

  setDraftPrintingType = (printingType: string) => {
    this.draftPrintingType = printingType ? toPodPrintingTypeName(printingType) : "";
  };

  applyFilters = () => {
    this.setParams({
      order_ids: [...this.draftOrderIds],
      printing_type: this.draftPrintingType,
      page: 1,
    });
  };

  handlePageChange = (page: number) => {
    this.setParams({ page });
  };

  handleRowsPerPageChange = (limit: number) => {
    this.setParams({ limit, page: 1 });
  };

  handleSortChange = (column: string) => {
    this.setParams(prev => ({
      ...prev,
      sort_by: column,
      sort_type: prev.sort_by === column && prev.sort_type === "DESC" ? "ASC" : "DESC",
      page: 1,
    }));
  };

  // --- Selection ---

  isOrderSelected = (order: PodOrder) =>
    this.selectedOrders.some(row => isSamePodOrderRow(row, order));

  clearSelection = () => {
    this.selectedOrders = [];
  };

  selectAll = () => {
    this.selectedOrders = [...this.podOrderListingResponse.list];
  };

  toggleRow = (order: PodOrder) => {
    this.selectedOrders = this.isOrderSelected(order)
      ? this.selectedOrders.filter(row => !isSamePodOrderRow(row, order))
      : [...this.selectedOrders, order];
  };

  areAllSelected = () => {
    const list = this.podOrderListingResponse.list;
    return list.length > 0 && list.every(order => this.isOrderSelected(order));
  };

  areSomeSelected = () => {
    const list = this.podOrderListingResponse.list;
    const selectedOnPage = list.filter(order => this.isOrderSelected(order)).length;
    return selectedOnPage > 0 && selectedOnPage < list.length;
  };

  setConfirmLoading = (loading: boolean) => {
    this.confirmLoading = loading;
  };

  // --- CSV import ---

  setPodOrderCsvFile = (file: File | null) => {
    this.podOrderCsvFile = file;
    this.podOrderCsvImportResult = null;
  };

  clearPodOrderCsvFile = () => {
    this.podOrderCsvFile = null;
    this.podOrderCsvImportResult = null;
  };

  setPodOrderCsvImportResult = (result: PodOrderCsvImportResult | null) => {
    this.podOrderCsvImportResult = result;
  };

  clearPodOrderCsvImportResult = () => {
    this.podOrderCsvImportResult = null;
  };

  // --- Print outward drawer ---

  setPrintOutwardDrawerOpen = (open: boolean) => {
    this.printOutwardDrawerOpen = open;
    if (!open) {
      this.printOutwardLines = [];
      this.printOutwardPrintingFiles = [];
      this.printOutwardFilesLoading = false;
      this.resetPrintOutwardForm();
      this.closePrintInwardMockupPreview();
    }
  };

  setPrintOutwardError = (message: string | null) => {
    this.printOutwardError = message;
  };

  openPrintOutwardDrawer = async (): Promise<boolean> => {
    if (this.selectedOrders.length === 0) return false;

    const baseLines: PodOrderPrintingFilesResponseItem[] = this.selectedOrders.map(
      order => ({
        order_id: order.order_id,
        product_id: order.product_id,
        stock_issue_request_id: order.stock_issue_request_id,
        sku: order.sku?.trim() ?? "",
        product_name: order.product_name,
        quantity: order.ordered_qty,
      }),
    );
    this.printOutwardLines = baseLines;
    this.printOutwardPrintingFiles = [];
    this.resetPrintOutwardForm();
    this.printOutwardDrawerOpen = true;
    this.printOutwardFilesLoading = true;

    const printingFileRequestItems = baseLines.filter(
      (line, index, list) =>
        list.findIndex(
          l => l.order_id === line.order_id && l.product_id === line.product_id,
        ) === index,
    );

    try {
      const response = await this.getPrintingFiles({
        items: printingFileRequestItems.map(({ order_id, product_id }) => ({
          order_id,
          product_id,
        })),
      });
      if (!response.success) {
        throw new Error(response.message || "Failed to fetch printing files.");
      }

      const apiItems: PodOrderPrintingFilesResponseItem[] =
        response.result?.items ?? [];

      const seenIds = new Set<number>();
      this.printOutwardPrintingFiles = apiItems.flatMap(item =>
        (item.printing_files ?? []).filter(file => {
          if (seenIds.has(file.id)) return false;
          seenIds.add(file.id);
          return true;
        }),
      );
      return true;
    } catch {
      this.printOutwardPrintingFiles = [];
      return false;
    } finally {
      this.printOutwardFilesLoading = false;
    }
  };

  resetPrintOutwardForm = () => {
    this.printOutwardPrinter = null;
    this.printOutwardDueDate = null;
    this.printOutwardNotes = "";
    this.printOutwardPrinterError = false;
    this.printOutwardDueDateError = false;
    this.printOutwardError = null;
  };

  setPrintOutwardPrinter = (printer: FilterValue | null) => {
    this.printOutwardPrinter = printer;
    if (printer) this.printOutwardPrinterError = false;
    this.printOutwardError = null;
  };

  setPrintOutwardDueDate = (date: Dayjs | null) => {
    this.printOutwardDueDate = date;
    if (date) this.printOutwardDueDateError = false;
    this.printOutwardError = null;
  };

  setPrintOutwardNotes = (notes: string) => {
    this.printOutwardNotes = notes;
    this.printOutwardError = null;
  };

  validatePrintOutwardForm = () => {
    const hasPrinter = Boolean(this.printOutwardPrinter?.id);
    const hasDueDate = Boolean(this.printOutwardDueDate);
    this.printOutwardPrinterError = !hasPrinter;
    this.printOutwardDueDateError = !hasDueDate;
    return hasPrinter && hasDueDate && this.printOutwardDueDate;
  };

  getPrintOutwardConfirmPayload = (): PodOrderPrintOutwardConfirmPayload => ({
    printer_id: Number(this.printOutwardPrinter!.id),
    timeline: this.printOutwardDueDate!.format("YYYY-MM-DD"),
    instructions: this.printOutwardNotes.trim(),
  });

  batchInventoryAllocateSelected = async (): Promise<APIResponse | null> => {
    if (this.selectedOrders.length === 0) return null;

    if (
      this.selectedOrders.some(order => order.stock_issue_request_id == null)
    ) {
      throw new Error(
        "Stock issue request ID is required for all selected rows.",
      );
    }

    const items = this.selectedOrders.map(order => ({
      stock_issue_request_id: order.stock_issue_request_id!,
    }));

    return this.batchInventoryAllocate({ items });
  };

  batchPrintOutwardSelected = async (
    payload: PodOrderPrintOutwardConfirmPayload,
  ): Promise<APIResponse | null> => {
    const lines =
      this.printOutwardLines.length > 0
        ? this.printOutwardLines
        : this.selectedOrders.map(order => ({
            order_id: order.order_id,
            product_id: order.product_id,
            stock_issue_request_id: order.stock_issue_request_id,
            sku: order.sku?.trim() ?? "",
            product_name: order.product_name,
            quantity: order.ordered_qty,
            printing_files: [] as PodOrderPrintingFilesResponseItem["printing_files"],
          }));

    const items = lines.map(line => ({
      order_id: line.order_id,
      product_id: line.product_id,
      sku: line.sku,
      quantity: line.quantity,
      ...(line.stock_issue_request_id != null
        ? { stock_issue_request_id: line.stock_issue_request_id }
        : {}),
    }));

    if (items.length === 0) return null;

    return this.batchPrintOutward({ items, ...payload });
  };

  // --- Print inward drawer ---

  setPrintInwardDrawerOpen = (open: boolean) => {
    this.printInwardDrawerOpen = open;
    if (!open) {
      this.printInwardLines = [];
      this.printInwardError = null;
      this.printInwardItems = new Map();
      this.printInwardRejectionFiles = new Map();
      this.printInwardRejectionNotesErrors = new Set();
      this.printInwardExpandedKey = null;
      this.closePrintInwardMockupPreview();
    }
  };

  setPrintInwardError = (message: string | null) => {
    this.printInwardError = message;
  };

  openPrintInwardDrawer = (): boolean => {
    if (this.selectedOrders.length === 0) return false;
    this.printInwardLines = [...this.selectedOrders];
    this.printInwardError = null;
    this.resetPrintInwardForm();
    this.printInwardExpandedKey = null;
    this.printInwardDrawerOpen = true;
    return true;
  };

  setPrintInwardExpandedKey = (key: string | null) => {
    this.printInwardExpandedKey = key;
  };

  togglePrintInwardExpandedKey = (key: string) => {
    this.printInwardExpandedKey = this.printInwardExpandedKey === key ? null : key;
  };

  openPrintInwardMockupPreview = (line: PodOrder) => {
    void this.openPodOrderMockupPreview(line);
  };

  openPrintOutwardMockupPreview = (line: PodOrderPrintingFilesResponseItem) => {
    void this.openPodOrderMockupPreview(line);
  };

  openPodOrderMockupPreview = async (line: PodOrderMockupPreviewLine) => {
    const sku = line.sku?.trim() ?? "";
    this.printInwardMockupPreviewOpen = true;
    this.printInwardMockupPreviewLoading = true;
    this.printInwardMockupPreviewMockups = [];
    this.printInwardMockupPreviewProduct = {
      id: line.product_id,
      name: line.product_name?.trim() || `Product #${line.product_id}`,
    };

    if (!sku) {
      this.printInwardMockupPreviewLoading = false;
      return;
    }

    try {
      const result = await this.getPodOrderMockups({
        order_id: line.order_id,
        product_id: line.product_id,
        sku,
      });
      this.printInwardMockupPreviewMockups = mapPodOrderMockupsToOrderMockups(
        result.mockups ?? [],
        line.order_id,
        line.product_id,
      );
    } catch {
      this.printInwardMockupPreviewMockups = [];
    } finally {
      this.printInwardMockupPreviewLoading = false;
    }
  };

  closePrintInwardMockupPreview = () => {
    this.printInwardMockupPreviewOpen = false;
    this.printInwardMockupPreviewLoading = false;
    this.printInwardMockupPreviewMockups = [];
    this.printInwardMockupPreviewProduct = null;
  };

  resetPrintInwardForm = () => {
    const next = new Map<string, PodOrderBatchPrintInwardItem>();
    const files = new Map<string, File[]>();
    const lines =
      this.printInwardLines.length > 0 ? this.printInwardLines : this.selectedOrders;

    lines.forEach(line => {
      if (line.print_outward_id == null) return;
      const key = getPodOrderRowKey(line);
      next.set(key, {
        order_id: line.order_id,
        product_id: line.product_id,
        print_outward_id: line.print_outward_id,
        quantity: Math.max(0, line.ordered_qty - Number(line.total_print_inwarded_qty ?? 0)),
        sku: line.sku?.trim() ?? "",
        printing_type: line.printing_type?.trim() ?? "",
        rejected_quantity: 0,
      });
      files.set(key, []);
    });

    this.printInwardItems = next;
    this.printInwardRejectionFiles = files;
    this.printInwardRejectionNotesErrors = new Set();
  };

  getPrintInwardLineItem = (line: PodOrder) =>
    this.printInwardItems.get(getPodOrderRowKey(line));

  getPrintInwardRejectionFiles = (line: PodOrder) =>
    this.printInwardRejectionFiles.get(getPodOrderRowKey(line)) ?? [];

  hasPrintInwardRejectionNotesError = (line: PodOrder) =>
    this.printInwardRejectionNotesErrors.has(getPodOrderRowKey(line));

  updatePrintInwardLine = (
    line: PodOrder,
    updates: Partial<
      Pick<PodOrderBatchPrintInwardItem, "quantity" | "rejected_quantity" | "rejection_reason">
    >,
  ) => {
    const key = getPodOrderRowKey(line);
    const current = this.printInwardItems.get(key);
    if (!current) return;

    const nextItems = new Map(this.printInwardItems);
    nextItems.set(key, { ...current, ...updates });
    this.printInwardItems = nextItems;
    this.printInwardError = null;

    const rejectedQty = updates.rejected_quantity ?? current.rejected_quantity;
    if (rejectedQty === 0) {
      this.printInwardRejectionNotesErrors.delete(key);
    }
  };

  setPrintInwardRejectionNotes = (line: PodOrder, rejection_reason: string) => {
    this.updatePrintInwardLine(line, { rejection_reason });
    this.printInwardRejectionNotesErrors.delete(getPodOrderRowKey(line));
  };

  setPrintInwardRejectionImages = (line: PodOrder, files: File[]) => {
    const key = getPodOrderRowKey(line);
    if (!this.printInwardItems.has(key)) return;
    const next = new Map(this.printInwardRejectionFiles);
    next.set(key, [...(next.get(key) ?? []), ...files]);
    this.printInwardRejectionFiles = next;
  };

  setPrintInwardRejectionImageUrls = (line: PodOrder, rejection_images: string[]) => {
    const key = getPodOrderRowKey(line);
    const current = this.printInwardItems.get(key);
    if (!current) return;
    const nextItems = new Map(this.printInwardItems);
    nextItems.set(key, { ...current, rejection_images });
    this.printInwardItems = nextItems;
  };

  removePrintInwardRejectionImage = (line: PodOrder, index: number) => {
    const key = getPodOrderRowKey(line);
    const next = new Map(this.printInwardRejectionFiles);
    next.set(
      key,
      (next.get(key) ?? []).filter((_, imageIndex) => imageIndex !== index),
    );
    this.printInwardRejectionFiles = next;
  };

  validatePrintInwardForm = () => {
    const errors = new Set<string>();

    this.printInwardItems.forEach((item, key) => {
      const requiresNotes =
        item.rejected_quantity > 0 && !(item.rejection_reason?.trim() ?? "");
      if (requiresNotes) errors.add(key);
    });

    this.printInwardRejectionNotesErrors = errors;
    return errors.size === 0;
  };

  batchPrintInwardSelected = async (): Promise<APIResponse | null> => {
    if (!this.validatePrintInwardForm()) return null;

    const items = Array.from(this.printInwardItems.values()).map(item => {
      const payload: PodOrderBatchPrintInwardItem = {
        order_id: item.order_id,
        product_id: item.product_id,
        print_outward_id: item.print_outward_id,
        quantity: item.quantity,
        sku: item.sku,
        printing_type: item.printing_type,
        rejected_quantity: item.rejected_quantity,
      };
      const reason = item.rejection_reason?.trim();
      if (reason) payload.rejection_reason = reason;
      if (item.rejection_images && item.rejection_images.length > 0) {
        payload.rejection_images = item.rejection_images;
      }
      return payload;
    });

    if (items.length === 0) return null;
    return this.batchPrintInward({ items });
  };
}
